import { parseDate } from '@openphone/lib/date'
import { action, computed, makeObservable, observable, toJS } from 'mobx'

import type { Model } from '@src/service/model'

export type AddonCreditsTier = 't1' | 't2' | 't3' | 't4' | 't5'

export interface AddonTierDataRaw {
  addonId: string
  orgId: string
  addonTier: AddonCreditsTier
  allottedCredits: number
  totalCreditsUsed: number
  overageCreditsUsed: number
  overageCreditCost: number
  overageCreditRate: number
  overagesEnabled: boolean
  usageResetsOn: string
  scheduledNextTier: AddonCreditsTier
  hasUnlimitedUsage: boolean
}

export class AddonTier implements Model {
  private raw: AddonTierDataRaw

  get id(): string {
    return this.raw.addonId
  }

  get addonTier(): AddonCreditsTier {
    return this.raw.addonTier
  }

  get hasUnlimitedUsage(): boolean {
    return this.raw.hasUnlimitedUsage
  }

  get scheduledNextTier(): AddonCreditsTier {
    return this.raw.scheduledNextTier
  }

  get allottedCredits(): number {
    return this.raw.allottedCredits
  }

  get totalCreditsUsed(): number {
    return this.raw.totalCreditsUsed
  }

  get remainingCredits(): number {
    return Math.max(this.raw.allottedCredits - this.raw.totalCreditsUsed, 0)
  }

  get overageCreditsUsed(): number {
    return this.raw.overageCreditsUsed
  }

  get overageCreditCost(): number {
    return parseFloat(this.raw.overageCreditCost.toFixed(2))
  }

  get overageCreditRate(): number {
    return parseFloat(this.raw.overageCreditRate.toFixed(2))
  }

  get overagesEnabled(): boolean {
    return this.raw.overagesEnabled
  }

  get usageResetsOn(): number {
    return parseDate(this.raw.usageResetsOn) ?? -1
  }

  constructor(attrs: AddonTierDataRaw) {
    this.raw = attrs

    makeObservable<this, 'raw' | 'localUpdate'>(this, {
      raw: observable.deep,
      addonTier: computed,
      allottedCredits: computed,
      totalCreditsUsed: computed,
      remainingCredits: computed,
      overageCreditsUsed: computed,
      overageCreditCost: computed,
      overageCreditRate: computed,
      overagesEnabled: computed,
      usageResetsOn: computed,
      localUpdate: action.bound,
    })
  }

  localUpdate(partial: Partial<AddonTierDataRaw>): this {
    this.raw = { ...this.raw, ...partial }
    return this
  }

  deserialize(json: AddonTierDataRaw): this {
    this.raw = json
    return this
  }

  serialize(): AddonTierDataRaw {
    return toJS(this.raw)
  }
}
